/**
 * THE RENEWALS SCREEN — a standalone destination, U5.5.
 *
 * ===========================================================================
 * WHAT THIS SCREEN IS FOR
 * ===========================================================================
 * An expired subscription nobody chased. Everything below is arranged around making that
 * impossible to miss: `Overdue` is a quick range of its own, an overdue row is painted in
 * the danger tone AND says how many days it has been lapsed AND carries a text label, and
 * the count in the header is the SERVER's so a full page can never read as "12 renewals" when
 * there are 400.
 *
 * ===========================================================================
 * ⚠ EVERY DATE ON THIS SCREEN COMES FROM THE SERVER, INCLUDING "TODAY"
 * ===========================================================================
 * `days_remaining` is computed in SQL against the COUNTRY's calendar day, and the resolved
 * window comes back in `range`. NOTHING here recomputes either from the browser clock. A
 * coordinator in Kinshasa looking at India's renewals is four and a half hours away from
 * India's "today"; a client-side week would label the table with one window while showing
 * the rows of another, for most of the day, every day.
 *
 * That is also why `formatDateOnly` below exists instead of reusing `formatDateLong`. See
 * its comment: the shared helper is right for timestamps and wrong for date-only columns.
 *
 * ===========================================================================
 * THE MONEY COLUMN IS THE ONE THAT CAN BE HIDDEN, AND ONLY IT
 * ===========================================================================
 * A seat without `amounts.view` receives the literal string `'•••'` in `line_total`. It is
 * rendered through `formatMoney`, which asks `isMaskedAmount` BEFORE it parses — because
 * `Number('•••')` is `NaN` and a renewals column rendering NaN, or worse 0, would tell a
 * manager a contract is worthless. Everything else on the row stays legible: that seat still
 * sees WHO, WHAT and WHEN, which is the entire point.
 *
 * ⚠ AND THERE IS NOW A THIRD ANSWER IN THAT COLUMN: `null`. Both sales sources price their
 * line, so until project maintenance arrived (Projects C7) a non-null `line_total` was true
 * in practice and the type said so. `project_maintenance.amount` is nullable — a term may be
 * recorded before anybody has priced the year — so the column must distinguish THREE
 * sentences: a figure, `•••` ("you may not see it"), and an em dash ("nobody has said").
 * `formatMoney`'s own null branch already returns `NO_VALUE`, so the em dash is not new
 * code; what changed is that the wire type stopped claiming null was impossible.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { NO_VALUE, formatMoney, isMaskedAmount } from '../inquiries/format';
import { daysLabel, formatDateOnly } from './format';
import { fetchCountryProducts, fetchQuoteCurrencies } from '../quotes/api';
import { cn } from '../../lib/cn';
// The honest-count pager is SHARED with the draft queue (U2/5.6). It moved out of this
// file unchanged — see `PagerFooter.tsx` for why one implementation rather than two.
import { PagerFooter } from './PagerFooter';
import {
  fetchRenewalsRegister,
  renewalDocumentPath,
  renewalsErrorText,
  renewalsKeys,
} from './api';
import { RENEWAL_SOURCES } from './api';
import type {
  RenewalRange,
  RenewalRegisterFilters,
  RenewalRegisterRow,
  RenewalSource,
  RenewalStatus,
} from './api';

const PER_PAGE = 50;

/**
 * WHAT EACH SOURCE IS CALLED ON SCREEN — `?source=`, the register's third filter.
 *
 * ⚠ A `Record<RenewalSource, …>`, NOT AN ARRAY OF PAIRS, AND THAT IS THE GATE. It is TOTAL
 * over the union, so a fourth source cannot be added to `RENEWAL_SOURCES` without somebody
 * deciding what an operator should call it — the same shape the API's own
 * `RENEWAL_SOURCE_FACTS` uses, for the same reason. An array would have compiled while
 * silently offering three options over four kinds of contract, which on a chase list reads
 * as "there is no such work".
 *
 * ⚠ AND THE ORDER OF THE OPTIONS IS `RENEWAL_SOURCES`' OWN, not this object's. Key order is
 * a property nobody should have to rely on; the render maps the exported list.
 */
const SOURCE_LABEL: Readonly<Record<RenewalSource, string>> = {
  so: 'Sales orders',
  quote: 'Quotes',
  /*
   * "Projects", not "Project maintenance": the operator's question is which KIND OF WORK a
   * contract came out of, and the answer beside "Sales orders" and "Quotes" is the work
   * type. The maintenance term is how a project produces a renewable year, not a fourth
   * category of thing to chase.
   */
  project: 'Projects',
};

/** The quick ranges, in the order the brief lists them. `custom` is a mode, not a chip. */
const RANGE_CHIPS: readonly { value: RenewalRange; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This week' },
  { value: 'next_week', label: 'Next week' },
  { value: 'this_month', label: 'This month' },
  // ⚠ THE OWNER'S ACCEPTANCE QUESTION HAS A CHIP. "Which of our apps have maintenance due
  // next month" is this chip plus the project source filter; leaving the range reachable
  // only by hand-editing the URL would make the accepted question an expert trick.
  { value: 'next_month', label: 'Next month' },
  { value: 'overdue', label: 'Overdue' },
];

/**
 * The status pills.
 *
 * ⚠ NO NEW COLOUR IS INVENTED — every pair already exists in `tokens.css`, which is what
 * keeps the AA contrast suites meaningful. `expired` takes the danger pair because an
 * unchased lapse is the failure this screen prevents; `superseded` is deliberately QUIET
 * (`bg-subtle`), because a contract the customer has already renewed is good news and
 * painting it as a problem sends somebody to chase a customer who has already paid.
 */
const STATUS_STYLE: Record<RenewalStatus, { label: string; classes: string }> = {
  expired: { label: 'Overdue', classes: 'bg-danger-bg text-danger-fg' },
  due: { label: 'Due', classes: 'bg-warn-bg text-warn-fg' },
  upcoming: { label: 'Upcoming', classes: 'bg-info-bg text-info-fg' },
  /*
   * ⚠ `text-fg`, NOT `text-fg-secondary` — MEASURED. Secondary on `bg-subtle` is 4.40:1 in
   * light mode, below the 4.5 AA floor for normal text. QUIET IS A PROPERTY OF THE
   * BACKGROUND, not of unreadable text: the neutral surface and the absent tone are what
   * make this pill calm, and dimming the label on top of that only makes it hard to read.
   * This is also the fallback-pill recipe the other registers already use.
   */
  superseded: { label: 'Renewed', classes: 'bg-subtle text-fg ring-1 ring-line' },
};

export function RenewalsPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  // The key the ROUTE enforces (`@RequirePerm('quotes.view')`), not a write key that would
  // hide the screen from a seat the API would serve. The Work Board lesson.
  const canView = can(countryCode, 'quotes.view');

  const [params, setParams] = useSearchParams();
  const rawRange = params.get('range');
  const range = rawRange === null || rawRange === '' ? 'this_month' : rawRange;
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const product = params.get('product') ?? '';
  /*
   * ⚠ PASSED THROUGH UNVALIDATED, exactly as `range` and `product` are, and the api.ts
   * header gives the reason: the server owns this vocabulary and answers a stranger with a
   * 400 that NAMES the allowed values. Sanitising here would render the WHOLE register
   * while the control claimed a filter — and on this screen an unfiltered list reads as
   * "nothing else is expiring".
   */
  const source = params.get('source') ?? '';
  const sort = params.get('sort') ?? 'expiry';
  const dir = params.get('dir') ?? 'asc';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const filters = useMemo<RenewalRegisterFilters>(
    () => ({
      range,
      from: from === '' ? undefined : from,
      to: to === '' ? undefined : to,
      product: product === '' ? undefined : product,
      source: source === '' ? undefined : source,
      sort,
      dir,
      page,
      perPage: PER_PAGE,
    }),
    [range, from, to, product, source, sort, dir, page],
  );

  const list = useQuery({
    queryKey: renewalsKeys.register(countryCode, filters),
    queryFn: ({ signal }) => fetchRenewalsRegister(countryCode, filters, signal),
    enabled: canView,
  });

  // The product filter's options. Country scope is its only gate, so any seat that can read
  // this register can read the catalogue it filters by.
  //
  // EVERY LIST, MERGED BY PRODUCT (2026-09-22). A country may keep one price list per quote
  // currency (Congo: CDF and USD), and a renewal can be for a product priced in either. The
  // bare call reads the home list only, which would silently drop a product priced only in
  // USD from the filter. Order: the home list's, then anything only another list carries.
  const products = useQuery({
    queryKey: ['renewals', countryCode, 'products'],
    queryFn: async ({ signal }) => {
      const { currencies } = await fetchQuoteCurrencies(countryCode, signal);
      const lists = await Promise.all(
        currencies.map((currency) => fetchCountryProducts(countryCode, signal, currency)),
      );
      const seen = new Set<string>();
      return lists.flat().filter((entry) => {
        if (seen.has(entry.product_id)) return false;
        seen.add(entry.product_id);
        return true;
      });
    },
    enabled: canView,
  });

  /** Filters REPLACE history; paging PUSHES it. Both reset the page. */
  const patch = (apply: (next: URLSearchParams) => void, replace: boolean): void => {
    const next = new URLSearchParams(params);
    apply(next);
    if (replace) next.delete('page');
    setParams(next, { replace });
  };

  const setRange = (value: RenewalRange): void => {
    patch((next) => {
      next.set('range', value);
      // A quick range has no custom bounds; leaving them would make the URL describe a
      // window the server did not apply.
      next.delete('from');
      next.delete('to');
    }, true);
  };

  const setCustom = (which: 'from' | 'to', value: string): void => {
    patch((next) => {
      next.set('range', 'custom');
      if (value === '') next.delete(which);
      else next.set(which, value);
    }, true);
  };

  const setProduct = (value: string): void => {
    patch((next) => {
      if (value === '') next.delete('product');
      else next.set('product', value);
    }, true);
  };

  const setSource = (value: string): void => {
    patch((next) => {
      if (value === '') next.delete('source');
      else next.set('source', value);
    }, true);
  };

  const setSort = (column: string): void => {
    patch((next) => {
      // Clicking the active column flips it; a new column starts ascending, which on a
      // renewals list means soonest-first — the order somebody chasing wants.
      const nextDir = sort === column && dir === 'asc' ? 'desc' : 'asc';
      next.set('sort', column);
      next.set('dir', nextDir);
    }, true);
  };

  const goToPage = (next: number): void => {
    const patched = new URLSearchParams(params);
    if (next <= 1) patched.delete('page');
    else patched.set('page', String(next));
    setParams(patched);
  };

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Renewals are not available to this account">
          Viewing renewals in {countryCode} needs the quotes permission. Ask an administrator
          to widen your access.
        </Alert>
      </div>
    );
  }

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;
  const resolved = list.data?.range;

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Renewals"
        subtitle={
          list.data === undefined
            ? undefined
            : `${String(total)} ${total === 1 ? 'contract' : 'contracts'}`
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Filter by expiry window" className="flex flex-wrap gap-2">
          {RANGE_CHIPS.map((chip) => (
            <RangeChip
              key={chip.value}
              label={chip.label}
              active={range === chip.value}
              danger={chip.value === 'overdue'}
              onClick={() => setRange(chip.value)}
            />
          ))}
        </div>

        <div className="flex items-center gap-2">
          <label className="text-body13 text-fg-secondary" htmlFor="renewals-from">
            From
          </label>
          <input
            id="renewals-from"
            type="date"
            value={from}
            onChange={(event) => setCustom('from', event.target.value)}
            className="h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg"
          />
          <label className="text-body13 text-fg-secondary" htmlFor="renewals-to">
            To
          </label>
          <input
            id="renewals-to"
            type="date"
            value={to}
            onChange={(event) => setCustom('to', event.target.value)}
            className="h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="text-body13 text-fg-secondary" htmlFor="renewals-product">
            Product
          </label>
          <select
            id="renewals-product"
            value={product}
            onChange={(event) => setProduct(event.target.value)}
            className="h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg"
          >
            <option value="">All products</option>
            {(products.data ?? []).map((entry) => (
              <option key={entry.product_id} value={entry.product_id}>
                {entry.product_name}
              </option>
            ))}
          </select>
        </div>

        {/*
          ⚠ WHICH KIND OF WORK THE CONTRACT CAME OUT OF — SERVER-SIDE, like every other
          control on this row. The register is paged, so a client-side segment would be
          filtering ONE page and calling it the register; the `?source=` parameter narrows
          the whole set and the header's total comes back describing it.

          It is a NARROWING, NEVER A GRANT: the server composes it with the per-seat
          visibility predicate, so a seat without `so.view` that asks for sales orders gets
          an honest empty register rather than rows it may not see.
        */}
        <div className="flex items-center gap-2">
          <label className="text-body13 text-fg-secondary" htmlFor="renewals-source">
            Source
          </label>
          <select
            id="renewals-source"
            value={source}
            onChange={(event) => setSource(event.target.value)}
            className="h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg"
          >
            <option value="">All sources</option>
            {RENEWAL_SOURCES.map((value) => (
              <option key={value} value={value}>
                {SOURCE_LABEL[value]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/*
        THE WINDOW THE SERVER APPLIED, IN WORDS. Not decoration: the chips say what was
        ASKED for and this says what was USED, resolved in the country's calendar. When
        those two can differ — and across a timezone they can — the operator needs the
        second one.
      */}
      {resolved !== undefined ? (
        <p className="text-body13 text-fg-secondary" data-testid="resolved-range">
          {resolved.from === null
            ? `Everything that expired before ${formatDateOnly(resolved.to)}`
            : `${formatDateOnly(resolved.from)} – ${formatDateOnly(resolved.to)}`}
        </p>
      ) : null}

      {list.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading renewals" />
        </div>
      ) : list.isError ? (
        <Alert tone="danger" title="Renewals could not be loaded">
          {/* The server's own sentence — on a 400 it names the vocabulary. */}
          <p>{renewalsErrorText(list.error)}</p>
          <button
            type="button"
            onClick={() => {
              void list.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : rows.length === 0 && total > 0 ? (
        /*
         * AN EMPTY PAGE IS NOT AN EMPTY RESULT SET, and on this screen conflating them
         * would say "no renewals are due" to somebody whose job is chasing the 400 that
         * are.
         */
        <Alert
          tone="info"
          title={`Page ${String(list.data.page)} is past the end of this register`}
        >
          <p>
            The register holds {String(total)} {total === 1 ? 'contract' : 'contracts'} in this
            window.
          </p>
          <button
            type="button"
            onClick={() => goToPage(1)}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Show the first page.
          </button>
        </Alert>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-surface">
          <EmptyState
            title={
              range === 'overdue'
                ? 'Nothing has expired unchased in this country.'
                : 'No contract expires in this window.'
            }
            action={
              range === 'this_month' && product === '' ? undefined : (
                <button
                  type="button"
                  onClick={() => {
                    const next = new URLSearchParams();
                    setParams(next, { replace: true });
                  }}
                  className="text-body13 font-medium text-indigo hover:underline"
                >
                  Clear the filters.
                </button>
              )
            }
          />
        </div>
      ) : (
        <RenewalsTable
          countryCode={countryCode}
          rows={rows}
          sort={sort}
          dir={dir}
          onSort={setSort}
        />
      )}

      {/* Every paging number below comes off the one payload the rows came off. */}
      {list.data !== undefined && rows.length > 0 ? (
        <PagerFooter
          page={list.data.page}
          perPage={list.data.perPage}
          shown={rows.length}
          total={list.data.total}
          onPage={goToPage}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── chips ── */

function RangeChip({
  label,
  active,
  danger,
  onClick,
}: {
  label: string;
  active: boolean;
  danger: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'h-8 rounded-sm border border-line bg-surface px-2.5 text-body13 text-fg-secondary',
        active && 'border-line-strong font-medium text-fg',
        // Overdue reads as urgent even when it is NOT the selected chip — the one range
        // somebody must notice they have not looked at.
        danger && (active ? 'bg-danger-bg text-danger-fg' : 'text-danger'),
      )}
    >
      {label}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────── table ── */

const COLUMNS: readonly { key: string; label: string; sortable: boolean }[] = [
  { key: 'customer', label: 'CUSTOMER', sortable: true },
  { key: 'product', label: 'PRODUCT', sortable: true },
  { key: 'start', label: 'START', sortable: true },
  { key: 'expiry', label: 'EXPIRY', sortable: true },
  { key: 'expiry', label: 'DAYS', sortable: false },
  { key: 'value', label: 'VALUE', sortable: true },
  { key: 'status', label: 'STATUS', sortable: true },
  { key: 'owner', label: 'OWNER', sortable: true },
];

function RenewalsTable({
  countryCode,
  rows,
  sort,
  dir,
  onSort,
}: {
  countryCode: string;
  rows: readonly RenewalRegisterRow[];
  sort: string;
  dir: string;
  onSort: (column: string) => void;
}): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[1180px] border-collapse text-left">
        <caption className="sr-only">
          Every subscription with an expiry date in {countryCode}
        </caption>
        <colgroup>
          <col className="w-[200px]" />
          <col />
          <col className="w-[120px]" />
          <col className="w-[120px]" />
          <col className="w-[120px]" />
          <col className="w-[130px]" />
          <col className="w-[110px]" />
          <col className="w-[150px]" />
          <col className="w-[150px]" />
        </colgroup>

        {/*
          ⚠ THE COLUMN HEADERS ARE `text-fg`, NOT `text-fg-muted`, AND THAT IS A
          MEASUREMENT RATHER THAN A PREFERENCE.

          This `<tr>` sets its own tint, so the headers do NOT sit on `bg/surface` like the
          rest of the table — they sit on `bg/subtle`, where BOTH of the app's dimmer text
          tokens fail AA in light mode:

              text/muted     #9aa0aa on #f7f9fb → 2.49:1   (what shipped)
              text/secondary #6e7580 on #f7f9fb → 4.40:1   (the obvious swap — ALSO fails)
              text/primary   #1c1c1c on #f7f9fb → 16.14:1  (this)

          `--text-th` is 11px at weight 500, so there is no WCAG large-text excuse (large =
          ≥18.66px at ≥700, or ≥24px). These labels name the columns; a register whose
          column names cannot be read is a register nobody can use.

          QUIET IS A PROPERTY OF THE BACKGROUND — the same argument `RenewalDraftsPage`'s
          recipient block already ships on this exact tint. The tint is what separates the
          head from the body; hierarchy is carried by SIZE, WEIGHT, CASE and TRACKING, all
          of which survive greyscale as colour does not.

          ⚠ THE SORT BUTTON'S HOVER MOVED WITH IT. It used to be `text-fg-muted
          hover:text-fg`, which becomes a no-op once the rest state IS `text-fg`; the
          affordance is now `hover:underline`, the app's existing idiom for interactive
          text. Losing the hover cue silently would have been the worse trade.

          ⚠ FILED, NOT FIXED — AND THIS NOTE HAS BEEN CORRECTED, BECAUSE THE FIRST VERSION
          WOULD HAVE SENT A FOLLOW-UP UNIT AT THE WRONG SET. It said "34 further sites across
          7 screens wear the identical `bg-subtle` thead pair". RE-COUNTED HERE:

            · 34 CODE sites carry `text-th uppercase text-fg-muted`, across 15 FILES, not 7.
              (35 matches repo-wide; the 35th is this comment.)
            · Only 19 of them are on a `<th>` — CrfsListPage 1, DeployedProjectsPage 6,
              SignoffQueuePage 6, DeploymentRegisterPage 2, DlFilesRegisterPage 2,
              TestingRegisterPage 2 — so the "identical thead pair" is a MINORITY of the count.
            · The other 15 are property labels, Sidebar section titles and a
              DropdownMenu.Label: components/shell/Sidebar.tsx, CrfRecordPage ×3,
              InquiryRecordPage ×2, PeekPanel ×2, inquiries/panel.tsx, InquiryTable,
              InquiriesNeedingActionWidget, AgentKeysPage, NetworkRulesPage, and one each in
              CrfsListPage and DlFilesRegisterPage. Those sit on `bg-surface`, where the same
              token measures 2.63:1 rather than 2.49:1 — a different number and, on a
              persistent shell element, a wider blast radius.
            · InquiryTable is the trap: its column headers build the class through `cn()`
              (`active ? 'text-fg' : 'text-fg-muted hover:text-fg-secondary'`), so a grep for
              the contiguous string CANNOT see them. Its one contiguous match is a dropdown
              label. Anyone acting on a grep count will miss that whole family.

          Reaching into them from this unit is still rule-6 scope expansion; they are
          reported, now with a count somebody can act on.
        */}
        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            {COLUMNS.map((column, index) => {
              const active = column.sortable && sort === column.key;
              return (
                <th
                  key={`${column.key}-${String(index)}`}
                  scope="col"
                  aria-sort={
                    active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                  className="px-3 text-th uppercase text-fg"
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(column.key)}
                      className="text-th uppercase text-fg hover:underline"
                    >
                      {column.label}
                      {active ? (
                        <>
                          {' '}
                          <span aria-hidden="true">{dir === 'asc' ? '↑' : '↓'}</span>
                          <span className="sr-only">
                            , sorted {dir === 'asc' ? 'ascending' : 'descending'}
                          </span>
                        </>
                      ) : null}
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              );
            })}
            <th scope="col" className="px-3">
              <span className="sr-only">Row actions</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => {
            const style = STATUS_STYLE[row.status];
            const overdue = row.status === 'expired';
            // `null` = this build has no screen for that document type. See the drilldown.
            const documentPath = renewalDocumentPath(countryCode, row.document_type, row.document_id);
            return (
              <tr
                key={`${row.source}:${row.line_id}`}
                data-status={row.status}
                data-source={row.source}
                className={cn(
                  'crf-register-row border-b border-line last:border-b-0 hover:bg-subtle',
                  /*
                   * ⚠ OVERDUE IS UNMISTAKABLE VIA A LEFT RULE, NOT A ROW TINT — AND THE
                   * FIRST VERSION WAS A MEASURED DARK-MODE FAILURE.
                   *
                   * `bg-danger-bg/40` looked right in light mode and was an accessibility
                   * defect in dark. `--crf-status-danger-bg` is MODE-INVARIANT (a pale pink
                   * in both themes, because it is designed to sit UNDER `danger-fg` text in
                   * a pill). Composited at 40% over the dark surface it produced
                   * rgb(119,109,109) — a mid grey — and every text colour on the row was
                   * measured against it in the running app:
                   *
                   *     customer link  1.19:1      days text  1.10:1
                   *     owner text     1.78:1      body text  4.59:1
                   *
                   * Three hard AA failures on the ONE row the whole screen exists to make
                   * noticeable. A left rule changes no text background at all, so every
                   * contrast on the row stays exactly what it is on a healthy row, and the
                   * cue is still visible the width of the table away.
                   *
                   * The signal is deliberately THREEFOLD and not colour alone: this rule,
                   * the pill reading "Overdue", and the days cell saying "34 days ago" in
                   * words. Colour is lost entirely in greyscale and to a colour-blind
                   * reader; the words are not.
                   */
                  overdue && 'border-l-4 border-l-danger-fg',
                )}
              >
                <td className="px-3 py-3 align-middle">
                  <Link
                    to={`/countries/${countryCode}/parties/${row.party_id}`}
                    className="text-body font-medium text-indigo hover:underline"
                  >
                    {row.party_name}
                  </Link>
                </td>
                <td className="px-3 py-3 align-middle text-body text-fg">{row.product_name}</td>
                <td className="tnum px-3 py-3 align-middle text-body13 text-fg-secondary">
                  {formatDateOnly(row.term_start)}
                </td>
                <td className="tnum px-3 py-3 align-middle text-body13 text-fg">
                  {formatDateOnly(row.expiry_date)}
                </td>
                <td
                  className={cn(
                    'tnum px-3 py-3 align-middle text-body13',
                    /*
                     * ⚠ `text-fg`, NOT `text-danger-fg`. `--crf-status-danger-text` is
                     * #ca2525 and is AA only against the PALE danger background it is
                     * paired with; on the dark table surface it measured 2.98:1 in the
                     * running app. Weight carries the emphasis instead, and the red lives
                     * where it is legal — the pill and the left rule.
                     */
                    overdue ? 'font-medium text-fg' : 'text-fg-secondary',
                  )}
                >
                  {daysLabel(row.days_remaining)}
                </td>
                <MoneyCell value={row.line_total} currency={row.currency} />
                <td className="px-3 py-3 align-middle">
                  <span
                    data-testid="status-pill"
                    className={cn(
                      'inline-flex h-6 items-center rounded-pill px-2 text-body13',
                      style.classes,
                    )}
                  >
                    {style.label}
                  </span>
                </td>
                <td className="px-3 py-3 align-middle text-body13 text-fg-secondary">
                  {row.owner_name ?? NO_VALUE}
                </td>
                <td className="px-3 py-3 align-middle">
                  {/*
                    THE DRILLDOWN, AND IT FOLLOWS THE SOURCE. A Tally Sales subscription's
                    originating document is a QUOTE, not a sales order (U5.4) — linking
                    every row to /sales-orders would give half of them a dead link, which
                    is worse than no link because the operator would believe the document
                    was missing.

                    ⚠ THE TERNARY IS GONE AND `renewalDocumentPath` DOES THE CHOOSING. Its
                    `else` arm was reachable by any document type that was not a sales
                    order, so it produced exactly the dead link this comment forbids — for
                    a THIRD type it would have linked to /quotes/<not-a-quote-id>. The
                    helper refuses at compile time and returns null at runtime, and null
                    renders the doc number as PLAIN TEXT: no link is the honest answer when
                    this build does not know where the document lives.
                  */}
                  {documentPath === null ? (
                    <span
                      className="text-body13 font-medium text-fg"
                      title="This build does not know which screen shows this document."
                    >
                      {row.document_doc_no}
                    </span>
                  ) : (
                    <Link
                      to={documentPath}
                      className="text-body13 font-medium text-indigo hover:underline"
                    >
                      {row.document_doc_no}
                    </Link>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The masked-cell idiom, verbatim from `CrfsListPage`'s `MoneyCell`: masked renders QUIETER
 * than a real figure, with the reason in a `title`, and the value is NEVER parsed.
 *
 * ⚠ THE QUIET TOKEN IS `text/secondary`, AND IT USED TO BE `text/muted`. That was a
 * MEASURED AA failure — `#9aa0aa` on `bg/surface` is **2.63:1** in light mode, and 2.49:1
 * once the row is hovered onto `bg/subtle`; the token set's own suite classes `text/muted`
 * as DECORATION and pins it under 3:1. `•••` is not decoration: it is the cell's entire
 * content and the only thing telling a no-amounts seat that a figure exists and is being
 * withheld. `text/secondary` measures 4.64 light / 5.85 dark on this surface — quieter than
 * the `text-fg` a real figure gets, so the distinction survives — and clears the floor.
 *
 * ⚠ WHAT THIS DOES NOT FIX, STATED RATHER THAN LEFT TO BE DISCOVERED: on a HOVERED row the
 * background becomes `bg/subtle` and the same pair measures **4.40:1** in light. Closing
 * that needs either `text-fg` (which erases the masked/unmasked distinction this cell
 * exists to draw) or a nudge to `--crf-text-muted`/`--crf-text-secondary` against the Figma
 * mirror — an owner's call, filed, not taken here.
 *
 * ⚠ A NULL VALUE IS NOT A MASKED VALUE, AND THE `title` IS WHERE THAT SHOWS. `isMaskedAmount`
 * is false for null, so an unpriced project term gets `formatMoney`'s em dash with NO tooltip
 * — it is not being withheld from anybody, nobody has entered it. Broadening the predicate to
 * cover null would tell a Country Manager holding `amounts.view` that a figure exists and is
 * being kept from them, which is a different and worse untruth than the one the mask prevents.
 */
function MoneyCell({
  value,
  currency,
}: {
  value: string | null;
  currency: string | null;
}): React.JSX.Element {
  const masked = isMaskedAmount(value);
  return (
    <td className="px-3 py-3 text-right align-middle">
      <span
        className={cn('tnum text-body', masked ? 'text-fg-secondary' : 'text-fg')}
        title={masked ? 'Hidden — this account cannot view amounts in this country' : undefined}
      >
        {formatMoney(value, currency)}
      </span>
    </td>
  );
}

/* ────────────────────────────────────────────────────────────────── pager ── */

/*
 * `PagerFooter` and its `PageButton` LEFT THIS FILE UNCHANGED and now live in
 * `./PagerFooter.tsx`, because the draft queue needs the same honest count and the rule
 * "a truncated list SAYS it is truncated" should have one implementation to be true of.
 */
